<template>
  <v-card
    variant="elevated"
    class="mx-auto bg-accent flex d-flex flex-column"
    style="border-radius: 25px"
    width="100%"
    height="100%"
  >
    <div>
      <v-img
        aspect-ratio="16/9"
        cover
        :src="item.imgUrl ? item.imgUrl : defaultImage"
        class="rounded-img position-relative"
      >
        <v-overlay
          contained
          :absolute="true"
          :model-value="!item.isBookable || !item.isPublic"
          class="align-start justify-start"
        >
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-icon
                v-if="!item.isPublic"
                class="ml-2"
                v-bind="props"
                color="white"
                size="75"
                >mdi-eye-off-outline</v-icon
              >
            </template>
            <span
              >Das Buchungsobjekt ist für die Öffentlichkeit nicht
              sichtbar</span
            >
          </v-tooltip>
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-icon
                v-if="!item.isBookable"
                class="ml-2"
                v-bind="props"
                color="white"
                size="75"
                >mdi-cancel</v-icon
              >
            </template>
            <span>Das Buchungsobjekt ist nicht buchbar</span>
          </v-tooltip>
        </v-overlay>
      </v-img>
    </div>
    <v-card-title>{{ item.title }}</v-card-title>
    <v-card-text
      v-if="item._populated?.event?.information?.name || item.description"
      class="pb-4"
    >
      <v-row no-gutters v-if="item.type === 'ticket'">
        <v-col cols="4" class="font-weight-bold">
          <small>Veranstaltung:</small>
        </v-col>
        <v-col>
          {{ item._populated?.event?.information?.name || "Unbekannt" }}
        </v-col>
      </v-row>

      <p
        class="mt-2 mb-0 text-wrap"
        v-if="item.description"
        v-html="shortenText(item.description)"
      ></p>
    </v-card-text>

    <v-card-text
      class="font-weight-bold pb-0"
      color="grey-darken-1"
      v-if="
        item.priceCategories &&
        item.priceCategories.some((pC) => pC.priceEur) > 0
      "
    >
      <v-row
        dense
        v-for="(priceCategory, index) in item.priceCategories"
        :key="index"
      >
        <v-col cols="6" md="3">
          {{ formatCurrency(priceCategory?.priceEur) }}
        </v-col>

        <v-col
          v-if="priceCategory.interval.end || priceCategory.interval.start"
        >
          {{
            getPrice(
              priceCategory.interval.start,
              priceCategory.interval.end,
              item.priceType
            )
          }}
        </v-col>

        <v-col>
          <v-chip
            variant="flat"
            v-if="priceCategory.fixedPrice"
            size="small"
            color="secondary"
          >
            Pauschalpreis
          </v-chip>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text
      class="font-weight-bold text-h6 pb-0"
      color="grey-darken-1"
      v-else
    >
      Kostenfrei
    </v-card-text>

    <v-card-text color="grey-darken-1" v-if="item.flags">
      <ul class="unstyled-list">
        <li v-for="flag in item.flags" :key="flag" class="d-flex">
          <v-icon class="pe-2" size="23">mdi-check</v-icon>
          <div class="text-subtitle-1">{{ flag }}</div>
        </li>
      </ul>
    </v-card-text>

    <v-card-actions class="py-4 mt-auto" v-if="editRoute">
      <v-spacer></v-spacer>
      <v-btn
        class="bg-secondary"
        color="black"
        rounded
        variant="text"
        :to="{ name: editRoute, query: { id: item.id, fromRoute: fromRoute } }"
        :disabled="!BookablePermissionService.allowUpdate(item)"
      >
        Bearbeiten
      </v-btn>
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ on: tooltip }">
              <v-btn icon density="comfortable" variant="text" v-bind="props">
                <v-icon size="25">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <span>Einstellungen</span>
          </v-tooltip>
        </template>
        <v-list density="compact">
          <v-list-item
            link
            @click="emitDuplicateAction"
            :disabled="duplicateDisabled"
            prepend-icon="mdi-content-copy"
          >
            <v-list-item-title>Duplizieren</v-list-item-title>
          </v-list-item>
          <v-list-item link @click="gotoCheckout" prepend-icon="mdi-cart">
            <v-list-item-title>Zur Buchung</v-list-item-title>
          </v-list-item>
          <v-divider
            v-if="BookablePermissionService.allowDelete(item)"
          ></v-divider>
          <v-list-item
            link
            class="text-red"
            @click="emitDeleteAction"
            :disabled="!BookablePermissionService.allowUpdate(item)"
            v-if="BookablePermissionService.allowDelete(item)"
            prepend-icon="mdi-delete"
          >
            <v-list-item-title>Löschen</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import BookablePermissionService from "@/services/permissions/BookablePermissionService";
import ApiBookablesService from "@/services/api/ApiBookablesService";
import FormatService from "@/services/FormatService";

export default {
  props: {
    editRoute: String,
    fromRoute: String,
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultImage: require("@/assets/bookable-default.jpg"),
      isDuplicateAllowed: true,
    };
  },
  methods: {
    getPrice(start, end, priceType) {
      const suffix = this.intervalSuffix(priceType);
      let interval = "";
      if (!start) {
        interval = `bis ${end}`;
      }
      if (!end) {
        interval = `ab ${start}`;
      }
      if (start && end) {
        interval = `${start} - ${end}`;
      }
      return `${interval} ${suffix}`;
    },
    formatCurrency(amount) {
      return FormatService.currency(amount, "EUR", "de-DE");
    },
    intervalSuffix(type) {
      if (type === "per-hour") {
        return "Std.";
      } else if (type === "per-day") {
        return "Tage";
      } else if (type === "per-square-meter") return "m²";
      else {
        return "Stück";
      }
    },

    emitDeleteAction() {
      this.$emit("delete");
    },
    emitDuplicateAction() {
      this.$emit("duplicate");
    },
    gotoCheckout() {
      const routeData = this.$router.resolve({
        name: "checkout",
        query: { id: this.item.id, tenant: this.tenantId, amount: 1 },
      });
      window.open(routeData.href, "_blank");
    },
    shortenText(text) {
      return text.substring(0, 140) + (text.length > 140 ? " ..." : "");
    },
    async setAllowDuplicate() {
      const bookableCountCheck =
        await ApiBookablesService.publicBookableCountCheck();
      this.isDuplicateAllowed = bookableCountCheck || !this.item.isPublic;
    },
  },
  computed: {
    ...mapGetters({
      tenantId: "tenants/currentTenantId",
    }),
    duplicateDisabled() {
      return (
        !this.BookablePermissionService.allowCreate() ||
        !this.isDuplicateAllowed
      );
    },
    BookablePermissionService() {
      return BookablePermissionService;
    },
  },
  mounted() {
    this.setAllowDuplicate();
  },
};
</script>

<style scoped lang="scss">
.text-wrap {
  white-space: normal !important;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}

.unstyled-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.rounded-img {
  border-top-left-radius: 25px !important;
  border-top-right-radius: 25px !important;
  height: 200px;
  max-height: 200px;
}
</style>
